import { useCallback, useMemo } from 'react';
import { CiCalculator2 } from 'react-icons/ci';

import {
  formatPercentage,
  formatPrice,
} from '../../../../helpers/NumberFormatter';
import './index.css';
import { Bot, Candle } from '../../types';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../../../colors';
import { Tabs } from '../../../ProfitCalculations/types';

interface Props {
  bot: Bot;
  latestPrice: Candle | undefined;
  earliestTimestamp: number | undefined;
}
const Price: React.FC<Props> = (props: Props) => {
  const { bot, latestPrice, earliestTimestamp } = props;

  const navigate = useNavigate();

  const deltaAbsoluteValue = useMemo(() => {
    if (!latestPrice) {
      return 0;
    }

    return latestPrice.close - latestPrice.open;
  }, [latestPrice]);

  const deltaRelativeValue = useMemo(() => {
    if (!latestPrice) {
      return 0;
    }

    return (latestPrice.close - latestPrice.open) / latestPrice.open;
  }, [latestPrice]);

  const deltaColor = useMemo(() => {
    if (!deltaAbsoluteValue) {
      return 'white';
    }

    return deltaAbsoluteValue > 0 ? 'green' : 'red';
  }, [deltaAbsoluteValue]);

  const latestPriceValue = useMemo(
    () => latestPrice?.close,
    [latestPrice?.close],
  );

  const handleChartPress = useCallback(() => {
    if (!earliestTimestamp) {
      return;
    }

    navigate('/profitCalculations', {
      state: {
        tab: Tabs.SingleProfitCalculation,
        baseAsset: bot.baseAsset,
        quoteAsset: bot.quoteAsset,
        startDate: earliestTimestamp,
        timeFrames: bot.timeFrame,
        complexParams: bot.complexParams,
      },
    });
  }, [
    bot.baseAsset,
    bot.complexParams,
    bot.quoteAsset,
    bot.timeFrame,
    earliestTimestamp,
    navigate,
  ]);

  if (!latestPriceValue) {
    return null;
  }

  return (
    <div className="price-root">
      <div className="price-container">
        {!!earliestTimestamp && (
          <div className="price-profit-icon" onClick={handleChartPress}>
            <CiCalculator2 color={colors.gray} size={30} fontWeight={800} />
          </div>
        )}
        <span className="price-value">
          {formatPrice(latestPriceValue, true, 7)}
        </span>
      </div>
      <div className="price-delta-container">
        <span className="price-delta-absolute" style={{ color: deltaColor }}>
          {formatPrice(deltaAbsoluteValue, true, 7)}
        </span>
        <span className="price-delta-relative" style={{ color: deltaColor }}>
          {formatPercentage(deltaRelativeValue)}
        </span>
      </div>
    </div>
  );
};

export default Price;
